import React, { useEffect, useState, useRef } from "react";
import { getGalleryList } from "services/gallery/gallery";
import { FaHeart, FaCalendarAlt } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./GalleryPage.scss";

// ==================== SINGLE EVENT CARD ====================
const GalleryEvent = ({ event, openLightbox }) => {
  const prevRef = useRef(null);
  const nextRef = useRef();



  return (
    <div className="event-item">
      {/* Header */}
      <div className="event-header">
        <div className="date-badge">
          <FaCalendarAlt />
          {new Date(event.date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </div>
        <h3 className="event-title">{event.title}</h3>
      </div>

      {/* Swiper Gallery */}
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        slidesPerView={1}
        loop={event.images.length > 1}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
        onSwiper={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
          swiper.navigation.init();
          swiper.navigation.update();
        }}
        className="gallery-swiper"
      >
        {event.images.map((img, i) => (
          <SwiperSlide key={i}>
            <div className="image-box" onClick={() => openLightbox(event, i)}>
              <img src={img.url} alt={event.title} loading="lazy" />
              <div className="image-overlay">
                <FaHeart className="heart-icon" />
                <span>Click to View Full Size</span>
              </div>
            </div>
          </SwiperSlide>
        ))}

        {/* Custom Navigation Buttons */}
        {event.images.length > 1 && (
          <>
            <div ref={prevRef} className="nav-btn prev-btn">‹</div>
            <div ref={nextRef} className="nav-btn next-btn">›</div>
          </>
        )}
      </Swiper>
    </div>
  );
};

// ==================== MAIN GALLERY PAGE ====================
const GalleryPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlides, setCurrentSlides] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [displayCount, setDisplayCount] = useState(4);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await getGalleryList({ page: 1, limit: 100 });
        if (res?.code === 200 && res?.data?.galleries) {
          const formatted = res.data.galleries.map((event) => ({
            id: event.id,
            title: event.title || "Moments of Love & Care",
            date: event.addedon,
            images: event.images.map((img) => ({ url: img.url })),
          }));
          setEvents(formatted);
        }
      } catch (err) {
        console.error("Gallery fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const openLightbox = (event, index) => {
    const slides = event.images.map((img) => ({
      src: img.url,
      title: event.title,
    }));
    setCurrentSlides(slides);
    setPhotoIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return (
      <div className="loader-center">
        <div className="spinner"></div>
        <p>Loading beautiful memories...</p>
      </div>
    );
  }

  return (
    <>
      <section className="gallery-hero-section">
        <div className="hero-content">
          <h1>
            Moments That Matter <FaHeart className="heart-icon" />
          </h1>
          <p>Every smile, every recovery, every celebration — stories of families we care for</p>
        </div>
      </section>


      {/* Gallery Grid */}
      <section className="gallery-grid-section">
        <div className="gallery-grid-container">
          {events.length === 0 ? (
            <div className="no-events">
              <h3>More memories coming soon...</h3>
            </div>
          ) : (
            events.slice(0, displayCount).map((event) => (
              <GalleryEvent key={event.id} event={event} openLightbox={openLightbox} />
            ))
          )}
        </div>

        {/* Load More Button */}
        {displayCount < events.length && (
          <div className="load-more-wrap">
            <button
              className="load-more-btn"
              onClick={() => setDisplayCount(displayCount + 4)}
            >
              Load More
            </button>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Need Care for Your Loved Ones?</h2>
        <p>We’re here 24×7 with love, trust & professional care</p>
        <div className="cta-buttons">
          <a href="tel:+918291008181" className="btn-primary">
            Call +91 82910 08181
          </a>
          <a href="/contactus" className="btn-secondary">
            Contact Us
          </a>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={photoIndex}
          slides={currentSlides}
          plugins={[Thumbnails, Zoom]}
          thumbnails={{
            width: 100,
            height: 70,
            gap: 12,
            borderRadius: 8,
          }}
        />
      )}
    </>
  );
};


export { GalleryEvent };
export default GalleryPage;